import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n";

export default function NewsletterForm({ variant = "hero" }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [values, setValues] = useState({ name: "", email: "", consent: false });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const next = {};
    if (!values.name.trim()) next.name = t("hero.validation.nameRequired");
    if (!values.email.trim()) next.email = t("hero.validation.emailRequired");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email))
      next.email = t("hero.validation.emailInvalid");
    if (!values.consent) next.consent = t("hero.validation.consentRequired");
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    navigate("/thank_you");
  };

  const idPrefix = variant === "hero" ? "hero" : "cta";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid={`newsletter-form-${idPrefix}`}
      noValidate
      className="space-y-4"
    >
      <div className="space-y-2">
        <Label
          htmlFor={`${idPrefix}-name`}
          className="text-xs tracking-[0.15em] uppercase text-slate-600"
        >
          {t("hero.nameLabel")}
        </Label>
        <Input
          id={`${idPrefix}-name`}
          data-testid={`${idPrefix}-newsletter-name`}
          value={values.name}
          onChange={(e) =>
            setValues((v) => ({ ...v, name: e.target.value }))
          }
          placeholder={t("hero.namePlaceholder")}
          className="rounded-sm border-slate-300 focus-visible:ring-[#C25934]"
          aria-invalid={!!errors.name}
        />
        {errors.name && (
          <p
            data-testid={`${idPrefix}-newsletter-name-error`}
            className="text-xs text-red-600"
          >
            {errors.name}
          </p>
        )}
      </div>
      <div className="space-y-2">
        <Label
          htmlFor={`${idPrefix}-email`}
          className="text-xs tracking-[0.15em] uppercase text-slate-600"
        >
          {t("hero.emailLabel")}
        </Label>
        <Input
          id={`${idPrefix}-email`}
          type="email"
          data-testid={`${idPrefix}-newsletter-email`}
          value={values.email}
          onChange={(e) =>
            setValues((v) => ({ ...v, email: e.target.value }))
          }
          placeholder={t("hero.emailPlaceholder")}
          className="rounded-sm border-slate-300 focus-visible:ring-[#C25934]"
          aria-invalid={!!errors.email}
        />
        {errors.email && (
          <p
            data-testid={`${idPrefix}-newsletter-email-error`}
            className="text-xs text-red-600"
          >
            {errors.email}
          </p>
        )}
      </div>
      <label className="flex items-start gap-3 text-xs text-slate-600 leading-relaxed">
        <input
          type="checkbox"
          checked={values.consent}
          onChange={(e) =>
            setValues((v) => ({ ...v, consent: e.target.checked }))
          }
          data-testid={`${idPrefix}-newsletter-consent`}
          className="mt-0.5 h-4 w-4 accent-[#C25934]"
        />
        <span>{t("hero.consent")}</span>
      </label>
      {errors.consent && (
        <p
          data-testid={`${idPrefix}-newsletter-consent-error`}
          className="text-xs text-red-600"
        >
          {errors.consent}
        </p>
      )}
      <Button
        type="submit"
        data-testid={`${idPrefix}-newsletter-submit`}
        disabled={submitting}
        className="w-full rounded-sm bg-[#C25934] hover:bg-[#a94a2c] text-white h-11"
      >
        {t("hero.submit")}
      </Button>
    </form>
  );
}
